import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useT } from '../i18n';
import './LocationPicker.css';

// ── LOCATION PICKER (Leaflet + Nominatim) ────────────────────────────────────
// Autocomplete input backed by OpenStreetMap's Nominatim geocoder, plus a map
// the user can click to place the pin directly. Chosen over Google Places
// because it needs no API key and no billing account.
//
// Two ways in, both ending at the same value:
//   • type  → forward geocode → pick a suggestion → map flies to it
//   • click → reverse geocode → input fills in with the place name
//
// Coordinates are captured alongside the display string. The string is what
// search filters group on (so it must stay stable), while lat/lng leaves the
// door open for "designers near me" without a migration later.
//
// NOMINATIM USAGE POLICY: max ~1 request/second, and no bulk querying. The
// 600ms debounce plus AbortController (which cancels the in-flight request on
// every new keystroke) keeps this comfortably inside that limit — without the
// abort, fast typing would fire a burst and risk the IP being blocked.
const NOMINATIM = 'https://nominatim.openstreetmap.org';
const DEBOUNCE_MS = 600;
const DEFAULT_CENTER = [40.1792, 44.4991];   // Yerevan
const DEFAULT_ZOOM = 11;

// Leaflet's default marker resolves its icon via image URLs that webpack
// rewrites, which is the classic "marker is invisible" bug. A divIcon sidesteps
// the bundler entirely — it's just markup we control.
const PIN_ICON = L.divIcon({
  className: 'lp-pin',
  html: '<span class="lp-pin-dot"></span>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// Nominatim returns long administrative chains ("Yerevan, Kentron, Armenia,
// 0001"). Search groups on this string, so it's trimmed to a stable
// "place, country" shape rather than stored verbatim.
function shortLabel(result) {
  const a = result.address || {};
  const place = a.city || a.town || a.village || a.municipality
    || a.county || a.state || result.name || '';
  const country = a.country || '';
  return [place, country].filter(Boolean).join(', ') || result.display_name || '';
}

// Recentres the map when a suggestion is picked. Has to be a child component —
// useMap only works inside MapContainer's tree.
function Recentre({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, Math.max(map.getZoom(), 13), { duration: 0.6 });
  }, [center, map]);
  return null;
}

function ClickCapture({ onPick }) {
  useMapEvents({ click: (e) => onPick([e.latlng.lat, e.latlng.lng]) });
  return null;
}

// ── TWO STRINGS FOR ONE PLACE ────────────────────────────────────────────────
// `value` is the SHORT label — "Yerevan, Armenia" — and it is what search
// groups and filters on (see locationsOf in designerService). It has to stay
// coarse: if every profile stored its own street address, the location filter
// would have one entry per user and match nothing.
//
// `address` is the FULL line Nominatim returned — "45, Khachatur Abovyan
// street, Arinj, Armenia". It is what the user actually chose and what they
// expect to see saved, so it is what the field displays and what a profile
// shows. Picking a suggestion writes both.
export default function LocationPicker({ value, address, coords, onChange, disabled = false }) {
  const { t } = useT();
  // The precise line when there is one, falling back to the short label for a
  // profile saved before addresses were kept.
  const shown = address || value || '';
  const [query, setQuery] = useState(shown);
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const [pin, setPin] = useState(coords?.lat ? [coords.lat, coords.lng] : null);
  const [centre, setCentre] = useState(coords?.lat ? [coords.lat, coords.lng] : null);

  // Suppresses the search that would otherwise fire from programmatically
  // setting the input — after a map click, a suggestion pick, or the parent
  // loading a saved value.
  const skipNextSearch = useRef(false);
  const abortRef = useRef(null);
  const timerRef = useRef(null);

  // Keep in sync when the parent replaces the value (e.g. Settings finishes
  // loading the saved profile after this component has already mounted).
  //
  // `skipNextSearch` is set alongside it: without that, loading a saved
  // location pushed text into `query`, which fired the search effect, which
  // opened the suggestion list — so the dropdown appeared on its own every
  // time Settings loaded, over a value the user had already chosen.
  useEffect(() => {
    skipNextSearch.current = true;
    setQuery(shown);
  }, [shown]);
  useEffect(() => {
    if (coords?.lat) { setPin([coords.lat, coords.lng]); setCentre([coords.lat, coords.lng]); }
  }, [coords?.lat, coords?.lng]);

  // Cancel any pending work on unmount so a late response can't setState on an
  // unmounted component.
  useEffect(() => () => {
    clearTimeout(timerRef.current);
    abortRef.current?.abort();
  }, []);

  // `label` is the search key, `full` is what the user picked. Both travel
  // together so the two can never drift apart — see the note on the props.
  const commit = useCallback((label, latlng, full) => {
    onChange({
      location: label,
      locationAddress: full || label || '',
      locationCoords: latlng ? { lat: latlng[0], lng: latlng[1] } : null,
    });
  }, [onChange]);

  // ── forward geocode (typing) ─────────────────────────────────────────────
  useEffect(() => {
    if (skipNextSearch.current) { skipNextSearch.current = false; return undefined; }
    const q = query.trim();
    if (q.length < 3) { setHits([]); return undefined; }

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setBusy(true);
      try {
        const url = `${NOMINATIM}/search?format=jsonv2&addressdetails=1&limit=6`
          + `&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { signal: ac.signal, headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('geocode failed');
        const data = await res.json();
        setHits(data.map(r => ({
          id: r.place_id,
          label: shortLabel(r),
          full: r.display_name,
          lat: Number(r.lat),
          lng: Number(r.lon),
        })));
        // Only reveal the list if the field still has focus — a late response
        // must not pop a dropdown over a form the user has moved on from.
        if (document.activeElement === inputRef.current) setOpen(true);
      } catch (err) {
        if (err.name !== 'AbortError') setHits([]);
      } finally {
        setBusy(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
  }, [query]);

  function pickSuggestion(h) {
    skipNextSearch.current = true;
    // The FULL line, not the short label. Picking "45, Khachatur Abovyan
    // street, Arinj" and watching the field collapse to "Arinj, Armenia" reads
    // as the app having thrown the address away — which it had.
    setQuery(h.full || h.label);
    setPin([h.lat, h.lng]);
    setCentre([h.lat, h.lng]);
    setHits([]);
    setOpen(false);
    commit(h.label, [h.lat, h.lng], h.full);
  }

  // ── reverse geocode (map click / marker drag) ────────────────────────────
  const pickPoint = useCallback(async (latlng) => {
    if (disabled) return;
    setPin(latlng);
    setBusy(true);
    try {
      const url = `${NOMINATIM}/reverse?format=jsonv2&addressdetails=1`
        + `&lat=${latlng[0]}&lon=${latlng[1]}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      const data = await res.json();
      const label = shortLabel(data);
      // Dropping a pin should name the spot as precisely as the map knows it,
      // for the same reason picking a suggestion does.
      const full = data.display_name || label;
      skipNextSearch.current = true;
      setQuery(full);
      setOpen(false);
      commit(label, latlng, full);
    } catch {
      // Reverse lookup failed — keep the pin and the coordinates, which are
      // still meaningful, and leave whatever the user had typed.
      commit(query.trim(), latlng, query.trim());
    } finally {
      setBusy(false);
    }
  }, [disabled, commit, query]);

  return (
    <div className="lp">
      <div className="lp-input-wrap">
        <input
          ref={inputRef}
          className="dpf-input"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => hits.length && setOpen(true)}
          // Delayed so a click on a suggestion lands before the list unmounts.
          onBlur={() => setTimeout(() => setOpen(false), 160)}
          placeholder={t('ds_location_ph')}
          disabled={disabled}
          autoComplete="off"
        />
        {busy && <span className="lp-spinner" aria-hidden="true" />}

        {open && hits.length > 0 && (
          <ul className="lp-ac">
            {hits.map(h => (
              <li key={h.id}>
                <button
                  type="button"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => pickSuggestion(h)}
                >
                  <span className="lp-ac-label">{h.label}</span>
                  <span className="lp-ac-full">{h.full}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={`lp-map${disabled ? ' lp-map--disabled' : ''}`}>
        <MapContainer
          center={centre || DEFAULT_CENTER}
          zoom={pin ? 13 : DEFAULT_ZOOM}
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Recentre center={centre} />
          <ClickCapture onPick={pickPoint} />
          {pin && (
            <Marker
              position={pin}
              icon={PIN_ICON}
              draggable={!disabled}
              eventHandlers={{ dragend: (e) => {
                const p = e.target.getLatLng();
                pickPoint([p.lat, p.lng]);
              } }}
            />
          )}
        </MapContainer>
      </div>
      <span className="dpf-hint">{t('ds_location_map_hint')}</span>
    </div>
  );
}
